<script lang="ts" setup>
import { computed, CSSProperties, Ref, ref, watch } from "vue";
import { PLoader } from "@profeat/ui-kit";
import { useElementBounding, useIntersectionObserver } from "@vueuse/core";
import {
  autoUpdate,
  useFloating,
  MaybeElement,
  offset,
} from "@floating-ui/vue";

import { defaultApi } from "~/api";
import { useContacts } from "~/stores/contacts.store";
import CustomersTableHeader from "./CustomersTableHeader.vue";

const headerHeight = 48;
const bodyRowHeight = 64;

const tableElement = ref<MaybeElement<Element>>();
const loaderTarget = ref(null);
const isFetching = ref(true);

const { height } = useElementBounding(tableElement as unknown as HTMLElement);
const contactsStore = useContacts();
const { x: loaderPosX, strategy } = useFloating(tableElement, loaderTarget, {
  strategy: "absolute",
  middleware: [offset(4)],
  whileElementsMounted: autoUpdate,
});

const props = defineProps({
  editableConfig: {
    type: Boolean,
    default: false,
    required: false,
  },
});

const emptyRows = computed(() => {
  const existBodyRowsHeight = contactsStore.getContacts.length * bodyRowHeight;
  const emptyHeight =
    Math.floor(height.value) - existBodyRowsHeight - headerHeight;

  return Array.from(
    {
      length: Math.floor(emptyHeight / bodyRowHeight),
    },
    (_, index) => index
  );
});
const loaderStyles = computed<CSSProperties>(() => {
  return {
    position: strategy.value,
    left: `${loaderPosX.value}px`,
  };
});
const headerFields = computed(() => {
  return contactsStore.getEntireFields.filter(
    (field) => field.options.visible !== false
  );
});
const isEmptyFilterResult = computed(() => {
  const { filtered } = contactsStore.getCount;
  const contactLength = contactsStore.getContacts.length;

  return (
    contactsStore.filterBuilder.filter.length > 0 &&
    contactLength === 0 &&
    filtered === contactLength
  );
});

const addFieldToTable = async ({
  fieldId,
  fieldGroup,
}: {
  fieldId: string;
  fieldGroup: "system" | "static" | "custom";
}) => {
  if (fieldGroup === "static") {
    return contactsStore.updateStaticFieldOptions({
      fieldId,
      options: {
        visible: true,
      },
    });
  }
  await contactsStore.updateFieldOptions([
    {
      fieldId,
      isSysField: fieldGroup === "system",
      visible: true,
    },
  ]);
};
const removeFieldFromTable = ({
  fieldId,
  fieldGroup,
}: {
  fieldId: string;
  fieldGroup: "system" | "static" | "custom";
}) => {
  if (fieldGroup === "static") {
    return contactsStore.updateStaticFieldOptions({
      fieldId,
      options: {
        visible: false,
      },
    });
  }
  contactsStore.updateFieldOptions([
    {
      fieldId,
      isSysField: fieldGroup === "system",
      visible: false,
    },
  ]);
};

watch(contactsStore.getContacts, () => {
  const contactLength = contactsStore.getContacts.length;
  const { all } = contactsStore.getCount;

  if (all === contactLength) {
    isFetching.value = false;
  } else if (contactLength < all) {
    isFetching.value = true;
  }
});
useIntersectionObserver(loaderTarget, async ([{ isIntersecting }]) => {
  if (isIntersecting) {
    const contactLength = contactsStore.getContacts.length;
    const { all } = contactsStore.getCount;

    if (all === contactLength) {
      isFetching.value = false;
      return;
    }

    await contactsStore.fetchContacts({
      skip: contactLength,
      take: defaultApi.take,
    });
  }
});
</script>

<template>
  <div class="table-wrapper">
    <div class="table-space" ref="tableElement">
      <table>
        <thead>
          <CustomersTableHeader
            :fields="headerFields"
            :editable-config="props.editableConfig"
            @add-field="addFieldToTable"
            @remove-field="removeFieldFromTable"
          />
        </thead>

        <tbody v-if="isEmptyFilterResult">
          <tr class="is-empty">
            <td :colspan="headerFields.length"></td>
          </tr>
        </tbody>
        <tbody v-else>
          <CustomersTableBody
            :contacts="contactsStore.getContacts"
            :header-fields="headerFields"
          />

          <tr v-for="count in emptyRows" :key="count" />
        </tbody>
      </table>

      <PLoader
        class="loader"
        v-show="
          isFetching &&
          contactsStore.getCount.filtered !== contactsStore.getContacts.length
        "
        ref="loaderTarget"
        :style="loaderStyles"
      />
    </div>

    <div v-if="isEmptyFilterResult" class="filter-empty-container">
      <h4 class="filter-empty-container__title">Контакты не найдены</h4>
      <p class="filter-empty-container__subtitle">
        Измените фильтры и повторите поиск
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table-wrapper {
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid var(--common-smog, #e5ebf0);

  .table-space {
    overflow: auto;
    min-height: 100px;
    height: 100%;
    position: relative;
    background: var(--common-white);
    z-index: 0;

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 25px;
      background: var(--common-rain, #d0dbe4);
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }

    table {
      width: 100%;
      border-collapse: collapse;

      thead {
        position: sticky;
        top: 0;
        background: var(--common-white);
        z-index: 1;

        tr {
          height: 48px;
        }

        &:before {
          content: "";
          position: absolute;
          bottom: 0;
          width: 100%;
          border-bottom: 1px solid var(--common-smog, #e5ebf0);
        }
      }
      :deep(tbody) {
        tr {
          // border-bottom: 1px solid var(--common-smog, #e5ebf0);
          height: 64px;
        }
      }
      tbody {
        tr {
          &.is-empty {
            height: calc(100vh - 64px - 70px - 32px - 48px - 32px - 4px);
          }
        }
      }
    }

    .loader {
      position: absolute;
      left: 50%;
      transform: translate(-50%, 0);
    }
  }
}
</style>

<style scoped>
.filter-empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: calc(50% + 48px);
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  z-index: 1;
}

.filter-empty-container__title {
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: var(--common-black);
}

.filter-empty-container__subtitle {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: var(--common-asphalt);
}
</style>
